import React, { useState, useRef, useEffect } from 'react';

export default function BuscadorDesplegable({ opciones, valor, onChange, placeholder, formatear, disabled }) {
  const [abierto, setAbierto]   = useState(false);
  const [busq, setBusq]         = useState('');
  const [proveedor, setProv]    = useState('todos');
  const [categoria, setCat]     = useState('todas');
  const ref = useRef(null);
  const listaRef = useRef(null);

  const seleccionado = opciones.find(o => o.id === valor);
  const [pos, setPos] = useState({ top:0, left:0, width:0 });

  /**
   * En el celular la lista se abre a pantalla completa, no colgada debajo del campo.
   *
   * Colgada se abría al fondo de la pantalla —el campo de producto queda abajo— y al salir el
   * teclado tapaba media pantalla: se escribía «mouse» y los resultados quedaban debajo del
   * teclado, sin forma de verlos, porque rodar la página la cerraba. A pantalla completa el
   * buscador queda arriba y la lista rueda sola. El corte es el mismo de Layout (900 px).
   */
  const [enHoja, setEnHoja] = useState(false);
  const enHojaRef = useRef(false);

  useEffect(() => {
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const cerrarScroll = (e) => {
      // A pantalla completa no hay nada detrás que rodar, y el teclado del celular dispara
      // «resize» al abrirse: cerrarla ahí la cerraba justo al ir a escribir.
      if (enHojaRef.current) return;
      // Cerrar solo si el scroll ocurre FUERA de la lista desplegable
      if (listaRef.current && listaRef.current.contains(e.target)) return;
      setAbierto(false);
    };
    document.addEventListener('mousedown', cerrar);
    window.addEventListener('scroll', cerrarScroll, true);
    window.addEventListener('resize', cerrarScroll);
    return () => {
      document.removeEventListener('mousedown', cerrar);
      window.removeEventListener('scroll', cerrarScroll, true);
      window.removeEventListener('resize', cerrarScroll);
    };
  }, []);

  const abrirConPos = () => {
    if (disabled) return;
    if (!abierto && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
      const hoja = window.innerWidth < 900;
      enHojaRef.current = hoja;
      setEnHoja(hoja);
    }
    setAbierto(a => !a);
    setBusq('');
  };

  const proveedores  = [...new Set(opciones.map(o => o.proveedor).filter(Boolean))].sort();
  const categorias   = [...new Set(opciones
    .filter(o => proveedor === 'todos' || o.proveedor === proveedor)
    .map(o => o.categoria).filter(Boolean))].sort();

  const filtrados = opciones.filter(o => {
    const matchProv = proveedor === 'todos' || o.proveedor === proveedor;
    const matchCat  = categoria === 'todas' || o.categoria === categoria;
    const matchBusq = !busq || formatear(o).toLowerCase().includes(busq.toLowerCase());
    return matchProv && matchCat && matchBusq;
  });

  // Agrupar por categoría
  const porCategoria = {};
  filtrados.forEach(o => {
    const cat = o.categoria || 'Sin categoría';
    if (!porCategoria[cat]) porCategoria[cat] = [];
    porCategoria[cat].push(o);
  });

  const provColor = {
    'Tecnoplaza': { background:'rgba(59,130,246,0.2)', color:'#93C5FD' },
    'Metro':      { background:'rgba(236,72,153,0.2)', color:'#F9A8D4' },
  };

  const s = {
    wrap:    { position:'relative', width:'100%', zIndex:'auto' },
    trigger: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'rgba(255,255,255,0.9)', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box', cursor:'pointer', fontFamily:'inherit', display:'flex', justifyContent:'space-between', alignItems:'center' },
    hoja:    { position:'fixed', top:0, left:0, right:0, bottom:0, background:'#0F1629', zIndex:99999, display:'flex', flexDirection:'column' },
    hojaCab: { display:'flex', alignItems:'center', gap:'10px', padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,0.1)' },
    cerrar:  { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'8px', padding:'8px 14px', color:'#fff', fontSize:'14px', cursor:'pointer', fontFamily:'inherit', flexShrink:0 },
    lista:   { position:'fixed', background:'#0F1629', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'10px', zIndex:99999, maxHeight:'400px', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.5)' },
    filtros: { padding:'8px', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', flexDirection:'column', gap:'6px' },
    busqRow: { display:'flex', flexWrap:'wrap', gap:'6px' },
    input:   { flex:'1 1 180px', minWidth:0, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'6px', padding:'7px 10px', color:'#fff', fontSize:'13px', outline:'none', fontFamily:'inherit' },
    sel:     { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'6px', padding:'7px 8px', color:'rgba(255,255,255,0.8)', fontSize:'12px', outline:'none', fontFamily:'inherit', cursor:'pointer' },
    catRow:  { display:'flex', gap:'4px', flexWrap:'wrap' },
    catBtn:  { padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600', border:'1px solid rgba(255,255,255,0.12)', background:'none', color:'rgba(255,255,255,0.45)', cursor:'pointer' },
    catBtnA: { background:'rgba(0,229,255,0.15)', color:'#00E5FF', border:'1px solid rgba(0,229,255,0.3)' },
    catHead: { padding:'6px 14px 3px', fontSize:'10px', fontWeight:'700', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', letterSpacing:'1px', background:'rgba(255,255,255,0.02)' },
    item:    { padding:'8px 14px', fontSize:'13px', color:'rgba(255,255,255,0.8)', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', justifyContent:'space-between', alignItems:'center' },
    badge:   { fontSize:'10px', padding:'1px 6px', borderRadius:'4px', fontWeight:'600', whiteSpace:'nowrap', marginLeft:'6px' },
    vacio:   { padding:'20px', fontSize:'13px', color:'rgba(255,255,255,0.3)', textAlign:'center' },
    limpiar: { padding:'8px 14px', fontSize:'12px', color:'rgba(255,255,255,0.35)', cursor:'pointer', textAlign:'center', borderTop:'1px solid rgba(255,255,255,0.06)' },
    total:   { padding:'6px 14px', fontSize:'11px', color:'rgba(255,255,255,0.3)', borderBottom:'1px solid rgba(255,255,255,0.06)' },
  };

  return (
    <div style={s.wrap} ref={ref}>
      <div style={s.trigger} onClick={abrirConPos}>
        <span style={{ color: seleccionado ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
          {seleccionado ? formatear(seleccionado) : placeholder}
        </span>
        <span style={{ color:'rgba(255,255,255,0.3)', marginLeft:'8px', fontSize:'11px' }}>{abierto?'▲':'▼'}</span>
      </div>

      {abierto && (
        <div ref={listaRef} style={enHoja ? s.hoja : {...s.lista, top: pos.top+'px', left: pos.left+'px', width: pos.width+'px'}}>
          {enHoja && (
            <div style={s.hojaCab}>
              <span style={{ flex:1, minWidth:0, fontSize:'15px', fontWeight:700, color:'#fff' }}>{(placeholder || 'Buscar').replace(/—/g, '').trim()}</span>
              <button type="button" style={s.cerrar} onClick={() => setAbierto(false)}>✕ Cerrar</button>
            </div>
          )}
          <div style={s.filtros}>
            {/* Fila 1: búsqueda + proveedor */}
            <div style={s.busqRow}>
              <input autoFocus style={s.input} placeholder="🔍 Buscar por nombre o SKU..."
                value={busq} onChange={e=>setBusq(e.target.value)} onClick={e=>e.stopPropagation()}/>
              {proveedores.length > 1 && (
                <select style={s.sel} value={proveedor}
                  onChange={e=>{ setProv(e.target.value); setCat('todas'); }}
                  onClick={e=>e.stopPropagation()}>
                  <option value="todos">Todos los proveedores</option>
                  {proveedores.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>
            {/* Fila 2: categorías como botones */}
            <div style={s.catRow} onClick={e=>e.stopPropagation()}>
              <button style={{...s.catBtn,...(categoria==='todas'?s.catBtnA:{})}}
                onClick={()=>setCat('todas')}>Todas</button>
              {categorias.map(c=>(
                <button key={c} style={{...s.catBtn,...(categoria===c?s.catBtnA:{})}}
                  onClick={()=>setCat(c)}>{c}</button>
              ))}
            </div>
          </div>

          {/* Total resultados */}
          <div style={s.total}>{filtrados.length} producto{filtrados.length!==1?'s':''} encontrado{filtrados.length!==1?'s':''}</div>

          <div style={enHoja ? { flex:1, overflowY:'auto', overscrollBehavior:'contain' } : undefined}>
          {/* Lista agrupada por categoría */}
          {filtrados.length === 0 ? (
            <div style={s.vacio}>Sin resultados{busq ? ` para "${busq}"` : ''}</div>
          ) : (
            Object.entries(porCategoria).sort(([a],[b])=>a.localeCompare(b)).map(([cat, items]) => (
              <div key={cat}>
                <div style={s.catHead}>{cat} ({items.length})</div>
                {items.map(o => (
                  <div key={o.id} style={enHoja ? { ...s.item, padding:'12px 14px', fontSize:'14px' } : s.item}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(0,212,255,0.07)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}
                    onClick={()=>{ onChange(o.id); setAbierto(false); setBusq(''); }}>
                    {/* En el celular el nombre se deja partir en renglones: cortado con «…» se
                        perdía justo el final, que es donde va el precio */}
                    <span style={enHoja ? {flex:1, minWidth:0, overflowWrap:'anywhere', marginRight:'8px'} : {flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginRight:'8px'}}>
                      {formatear(o)}
                    </span>
                    {o.proveedor && (
                      <span style={{...s.badge,...(provColor[o.proveedor]||{background:'rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.5)'})}}>
                        {o.proveedor}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}

          {valor && (
            <div style={s.limpiar} onClick={()=>{ onChange(''); setAbierto(false); }}>
              ✕ Limpiar selección
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
